import { useMemo } from 'react'
import { formatTimeAgo, isToday } from '../lib/format'
import { useFamilyData } from '../hooks/useFamilyData'
import RecordsList from './RecordsList'

interface Props {
  aiTextEnabled: boolean
  aiVisionEnabled: boolean
}

export default function DataPage({ aiTextEnabled, aiVisionEnabled }: Props) {
  const { records, stats, refreshing } = useFamilyData()

  // 派生指标（records 来自 Context，无新请求）
  const { todayCount, lastTime } = useMemo(() => {
    let t = 0
    let last: number | null = null
    for (const r of records) {
      if (isToday(r.occurred_at)) t++
      if (last === null || r.occurred_at > last) last = r.occurred_at
    }
    return { todayCount: t, lastTime: last }
  }, [records])

  const chartData = useMemo(() => {
    if (!stats) return []
    return Object.entries(stats.by_day)
      .slice(-7)
      .map(([day, d]) => ({ day, ...d }))
  }, [stats])

  const maxVal = Math.max(1, ...chartData.map((d) => d.total))

  return (
    <main>
      <section className="stats">
        <div className="stats-row stats-row-2">
          <div className="stat-card">
            <div className="stat-label">今日</div>
            <div className="stat-value">{todayCount}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">上次距今</div>
            <div className="stat-value">{lastTime ? formatTimeAgo(lastTime) : '--'}</div>
          </div>
        </div>

        {chartData.length > 0 && stats && stats.total > 0 && (
          <div className="chart-card">
            <div className="chart-header">
              <h3>近 7 天趋势</h3>
              <div className="chart-legend">
                <span><i className="dot dot-pee" />嘘嘘</span>
                <span><i className="dot dot-poop" />臭臭</span>
                <span><i className="dot dot-mixed" />混合</span>
                {refreshing && <span className="chart-syncing">同步中</span>}
              </div>
            </div>

            <Chart7Day data={chartData} maxVal={maxVal} />

            {aiTextEnabled && (
              <button
                className="btn-secondary ai-summary-btn"
                onClick={() => window.dispatchEvent(new CustomEvent('open-ai-summary'))}
              >
                🤖 AI 生成 7 天趋势总结
              </button>
            )}
          </div>
        )}
      </section>

      <RecordsList aiVisionEnabled={aiVisionEnabled} />
    </main>
  )
}

/**
 * 重做后的 7 天柱状图
 * - 更高（160px）
 * - 横向虚线网格（4 条）
 * - 柱顶数值标签
 * - Y 轴刻度（左侧）
 * - 高对比色（pee 深蓝 / poop 橙红 / mixed 紫）
 * - 圆角柱
 * - hover 显示详情
 */
function Chart7Day({ data, maxVal }: { data: { day: string; total: number; pee: number; poop: number; mixed: number }[]; maxVal: number }) {
  // Y 轴刻度：四等分（向上取整）
  const yTicks = useMemo(() => {
    const m = Math.max(maxVal, 1)
    return [m, Math.ceil((m * 3) / 4), Math.ceil(m / 2), Math.ceil(m / 4), 0]
  }, [maxVal])

  return (
    <div className="chart7">
      <div className="chart7-yaxis">
        {yTicks.map((v, i) => (
          <span key={i} className="chart7-ytick">{v}</span>
        ))}
      </div>
      <div className="chart7-canvas">
        {/* 横向网格线 */}
        <div className="chart7-grid">
          {yTicks.map((_, i) => (
            <div key={i} className="chart7-gridline" />
          ))}
        </div>
        <div className="chart7-bars">
          {data.map((d) => {
            const segs: { cls: string; n: number }[] = []
            if (d.pee > 0) segs.push({ cls: 'seg-pee', n: d.pee })
            if (d.poop > 0) segs.push({ cls: 'seg-poop', n: d.poop })
            if (d.mixed > 0) segs.push({ cls: 'seg-mixed', n: d.mixed })
            const heightPct = (d.total / maxVal) * 100
            return (
              <div
                key={d.day}
                className="chart7-bar-group"
                title={`${d.day}: 嘘 ${d.pee} · 臭 ${d.poop} · 混 ${d.mixed}`}
              >
                <div className="chart7-bar-value">{d.total || ''}</div>
                <div className="chart7-bar-track">
                  <div
                    className="chart7-bar-stack"
                    style={{ height: `${Math.max(heightPct, d.total > 0 ? 6 : 0)}%` }}
                  >
                    {segs.map((s, i) => (
                      <div key={i} className={`chart7-bar-seg ${s.cls}`} style={{ flex: s.n }} />
                    ))}
                  </div>
                </div>
                <div className="chart7-bar-label">{d.day}</div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
